import * as React from 'react';
import { connect } from 'react-redux';
import { getFormValues } from 'redux-form';
import { debounce } from 'throttle-debounce';
import * as escapeStringRegexp from 'escape-string-regexp';

import { RootState } from '../../../RootState';
import { FormData } from '../../../components/NetworksPage/Search/Search';
import { NetworksTableLocale } from '../../../components/NetworksPage/NetworksTable/networksTable.locale';
import { Network } from '../../../GraphQL';

const asFormat = /^[a,s]{0,2}[0-9]*$/i;
const debounceDelay = 100;

export interface FilteredNetwork extends Network {
  firstLetter?: string;
}

export interface FilteredNetworksProps {
  networks: FilteredNetwork[];
  locale: NetworksTableLocale;
}

interface StateProps {
  searchString: string;
}

interface OwnProps {
  networks: Network[];
  locale: NetworksTableLocale;
}

interface FilterNetworksProps extends StateProps, OwnProps {}

interface FilterNetworksState {
  searchString: string;
}

export function filterNetworks<P = {}>(
  WrappedComponent: React.ComponentType<P & FilteredNetworksProps>,
) {
  class FilterNetworks extends React.Component<FilterNetworksProps, FilterNetworksState> {
    state = {
      searchString: '',
    };

    setSearchString = debounce(debounceDelay, false, (searchString: string) =>
      this.setState({ searchString }),
    );

    componentWillReceiveProps(nextProps: FilterNetworksProps) {
      if (nextProps.searchString !== this.props.searchString) {
        this.setSearchString(escapeStringRegexp(nextProps.searchString.trim()));
      }
    }

    render() {
      const { searchString } = this.state;
      const { policyOptions } = this.props.locale;
      const fieldsToSearch = ['name'];
      const foundPolicies: string[] = [];
      const formatedPolicyOptions = { ...policyOptions };

      const mark = (text: string, position: number) =>
        `${text.substring(0, position)}<mark>${text.substring(
          position,
          position + searchString.length,
        )}</mark>${text.substring(position + searchString.length)}`;

      if (searchString) {
        if (asFormat.test(searchString)) {
          fieldsToSearch.push('asNumber');
        }

        Object.entries(policyOptions).forEach(([key, value]) => {
          const searchIndex = value.search(new RegExp(searchString, 'i'));
          if (searchIndex !== -1) {
            foundPolicies.push(key);
            formatedPolicyOptions[key] = mark(policyOptions[key], searchIndex);
          }
        });
      }

      let previousFirstLetter = '';
      const addFirstLetter = (network: Network): FilteredNetwork => {
        const currentFirstLetter = network.name[0].toUpperCase();
        if (currentFirstLetter !== previousFirstLetter) {
          previousFirstLetter = currentFirstLetter;
          return { ...network, firstLetter: currentFirstLetter };
        }

        return network;
      };

      const searchAndMark = (networksArray: FilteredNetwork[], network: Network) => {
        const filteredNetwork = {};
        let found = false;

        fieldsToSearch.forEach(key => {
          const searchIndex = network[key].search(new RegExp(searchString, 'i'));

          if (searchIndex !== -1) {
            filteredNetwork[key] = mark(network[key], searchIndex);
            found = true;
          }
        });

        if (found || foundPolicies.includes(network.peeringPolicy)) {
          networksArray.push({ ...addFirstLetter(network), ...filteredNetwork });
        }

        return networksArray;
      };

      const filteredNetworks = this.props.searchString
        ? this.props.networks.reduce(searchAndMark, [])
        : this.props.networks.map(addFirstLetter);

      return (
        <WrappedComponent
          {...this.props}
          locale={{ ...this.props.locale, policyOptions: formatedPolicyOptions }}
          networks={filteredNetworks}
        />
      );
    }
  }

  const mapStateToProps = (state: RootState) => {
    const formValues = getFormValues('networksSearch')(state) as FormData;

    return {
      searchString: formValues ? formValues.search : '',
    };
  };

  return connect<StateProps, {}, OwnProps, RootState>(mapStateToProps)(FilterNetworks);
}



// WEBPACK FOOTER //
// /www/client/src/containers/NetworksPage/NetworksTable/filterNetworks.tsx